import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import fr from '../i18n/fr.json';
import en from '../i18n/en.json';

export type Locale = 'fr' | 'en';

type Dict = Record<string, string>;

const STORAGE_KEY = 'dezoom_locale';
const dicts: Record<Locale, Dict> = { fr, en };

let currentLocale: Locale = 'fr';

function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'fr') return stored;
  } catch {
    /* ignore */
  }
  if (typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('en')) {
    return 'en';
  }
  return 'fr';
}

function applyDocumentLang(locale: Locale) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(locale: Locale): void {
  currentLocale = locale;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
  applyDocumentLang(locale);
}

function translate(locale: Locale, key: string): string {
  return dicts[locale][key] ?? dicts.fr[key] ?? key;
}

export function t(key: string): string {
  return translate(currentLocale, key);
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    currentLocale = detectLocale();
    applyDocumentLang(currentLocale);
    return currentLocale;
  });

  const changeLocale = useCallback((next: Locale) => {
    currentLocale = next;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    applyDocumentLang(next);
    setLocaleState(next);
  }, []);

  const value = useMemo(
    () => ({ locale, setLocale: changeLocale }),
    [locale, changeLocale],
  );

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale outside LocaleProvider');
  return ctx;
}

export function useT() {
  const { locale, setLocale } = useLocale();
  const translateFn = useCallback(
    (key: string) => translate(locale, key),
    [locale],
  );
  return { t: translateFn, locale, setLocale };
}
